import type { MessageEntity, MessageEntityType } from './types'

/**
 * Defensive runtime contract for Telegram-style text entities
 * (`attributes.entities`) — a malformed payload must never blank a message,
 * so everything here filters instead of throwing.
 */

export type MessageEntityNode = { kind: 'text'; text: string } | { kind: 'entity'; entity: MessageEntity; children: MessageEntityNode[] }

/**
 * Filter a raw `attributes.entities` value down to renderable entities:
 * unknown types, non-integer or out-of-bounds offsets, empty spans, and
 * partial overlaps are dropped. Returns entities sorted by `offset` ascending,
 * `length` descending (outermost first) — the order `buildEntityTree` expects.
 */
export const sanitizeEntities = (body: string, raw: unknown): MessageEntity[] => {
  if (!Array.isArray(raw)) return []
  const entities = raw.filter((item): item is MessageEntity => isWellFormedEntity(body, item))
  entities.sort((a, b) => a.offset - b.offset || b.length - a.length)

  const kept: MessageEntity[] = []
  const openEnds: number[] = []
  for (const entity of entities) {
    while (openEnds.length && openEnds[openEnds.length - 1] <= entity.offset) openEnds.pop()
    const end = entity.offset + entity.length
    if (openEnds.length && end > openEnds[openEnds.length - 1]) continue
    kept.push(entity)
    openEnds.push(end)
  }
  return kept
}

/**
 * Build the render tree for `body` + sanitized entities: text leaves
 * interleaved with entity nodes, contained entities nested as children.
 */
export const buildEntityTree = (body: string, entities: MessageEntity[]): MessageEntityNode[] => buildRange(body, 0, body.length, entities)

/** Whether `url` is an absolute http(s) URL — anything else is not linkable. */
export const isHttpUrl = (url: unknown): url is string => {
  if (typeof url !== 'string') return false
  try {
    const protocol = new URL(url).protocol
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

const ENTITY_TYPES: readonly MessageEntityType[] = ['bold', 'italic', 'underline', 'strikethrough', 'spoiler', 'code', 'pre', 'blockquote', 'text_link']

const isWellFormedEntity = (body: string, item: unknown): boolean => {
  if (typeof item !== 'object' || item === null) return false
  const { type, offset, length } = item as MessageEntity
  if (!ENTITY_TYPES.includes(type)) return false
  if (!Number.isInteger(offset) || !Number.isInteger(length)) return false
  return offset >= 0 && length > 0 && offset + length <= body.length
}

/** `entities` must be sorted (offset asc, length desc) and lie within [start, end). */
const buildRange = (body: string, start: number, end: number, entities: MessageEntity[]): MessageEntityNode[] => {
  const nodes: MessageEntityNode[] = []
  let cursor = start
  let index = 0
  while (index < entities.length) {
    const entity = entities[index]
    if (entity.offset > cursor) nodes.push({ kind: 'text', text: body.slice(cursor, entity.offset) })
    const entityEnd = entity.offset + entity.length
    const contained: MessageEntity[] = []
    let next = index + 1
    while (next < entities.length && entities[next].offset < entityEnd) contained.push(entities[next++])
    nodes.push({ kind: 'entity', entity, children: buildRange(body, entity.offset, entityEnd, contained) })
    cursor = entityEnd
    index = next
  }
  if (cursor < end) nodes.push({ kind: 'text', text: body.slice(cursor, end) })
  return nodes
}
